import { play } from 'cuelume'

import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import { baseCurrencyHint, dedupeTargets } from '@/features/order/order-form/lib/submitters/shared'
import type { SubmitterContext } from '@/features/order/order-form/lib/submitters/types'
import { trackOrderItems } from '@/lib/analytics-order'
import { toast } from '@/lib/toast'
import { randomUUID } from '@/lib/utils'
import type { useStartLimitChaseAlgo, useStartLimitChaseAlgoBatch } from '@/service/algo.hook'
import { LimitChaseAlgoParams } from '@/types/generated/algo'
import { Denomination, OrderSide } from '@/types/generated/common/trading'
import type { Instrument } from '@/types/generated/common/types'

export type SubmitLimitChaseDeps = {
  instruments: Instrument[]
  instrumentMap: Map<string, Instrument>
  startLimitChase: ReturnType<typeof useStartLimitChaseAlgo>['mutateAsync']
  startLimitChaseBatch: ReturnType<typeof useStartLimitChaseAlgoBatch>['mutateAsync']
}

// Submit LimitChase algo order (single or batch)
export async function submitLimitChaseOrder(ctx: SubmitterContext, deps: SubmitLimitChaseDeps) {
  const { t } = ctx
  const { instruments, instrumentMap, startLimitChase, startLimitChaseBatch } = deps
  if (!ctx.validateSubmissionRows()) return
  const rows = ctx.getSubmissionRows()

  if (rows.length === 0) {
    toast.error(t('selectAccountFirst'), { position: 'bottom-right' })
    return
  }

  const hasInvalidAmount = rows.some(({ rawItem }) => {
    const amount = rawItem.amount
    return !amount || amount <= 0
  })

  if (hasInvalidAmount) {
    toast.error(t('enterValidAmountAllItems'), { position: 'bottom-right' })
    return
  }

  ctx.setIsSubmitting(true)

  try {
    const paramsList: LimitChaseAlgoParams[] = rows.map(({ item, rawItem, account, exchange }) => {
      const amount = rawItem.amount ?? 0
      const amountUnit = item.amountUnit ?? ''
      const baseCurrency = resolveBaseCurrency(
        instrumentMap,
        item.symbol,
        baseCurrencyHint(item.baseCurrency)
      )
      const isBaseAmount = item.itemType === 'option' || amountUnit === baseCurrency

      const optionContractSize =
        item.itemType === 'option'
          ? Math.max(
              1,
              Number(instruments.find((i) => i.symbol === item.symbol)?.contractSize) || 1
            )
          : 1
      const effectiveAmount = amount * optionContractSize

      return {
        symbol: item.symbol,
        quantity: effectiveAmount.toString(),
        orderSide: item.side === 'buy' ? OrderSide.ORDER_SIDE_BUY : OrderSide.ORDER_SIDE_SELL,
        accountId: account.accountId,
        exchange,
        tradeVenue: account.tradeVenue,
        denomination: isBaseAmount
          ? Denomination.DENOMINATION_BASE
          : Denomination.DENOMINATION_QUOTE,
        // Advanced-TWAP per-slice knob; absent keeps standalone limit-chase behaviour.
        catchup: undefined,
      }
    })

    const lcSubmissionId = randomUUID()

    if (paramsList.length > 1) {
      await startLimitChaseBatch(paramsList.map((p) => ({ ...p, correlationId: lcSubmissionId })))
    } else {
      await startLimitChase({ ...paramsList[0], correlationId: lcSubmissionId })
    }

    trackOrderItems('limit_chase', paramsList, ctx.getAnalyticsAccounts(), 'order-form')
    const lcTargets = dedupeTargets(paramsList)
    const selection = ctx.getSelection()
    ctx.triggerOptimistic(lcSubmissionId, lcTargets, selection.instruments, selection.options)
    // No success toast — the WS algoLifecycle STARTED notification already
    // shows one (notification-manager.tsx), and firing both looked like a duplicate.
    ctx.resetFormAfterOrder()
  } catch (error) {
    toast.error(error instanceof Error ? error.message : t('limitChaseSubmissionFailed'), {
      position: 'bottom-right',
    })
    play('error')
    ctx.setIsSubmitting(false)
  }
}
